import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Link, MenuItem, Paper, Select, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SyncIcon from "@mui/icons-material/Sync";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { AppLayout } from "@/components/AppLayout";
import {
  fetchPaymentSync, postPaymentSyncBatch, rebuildPaymentSync,
  type PsMilestone, type PsPage, type PsRow,
} from "@/api/paymentSync";

// The AR team's review of the daily payment sync batch (PaymentSync in Rails):
// NetSuite cash customer payments and chargebacks, each matched to the Sunrise
// payment task it settles. Nothing changes in Sunrise until the reviewer posts,
// and the server re-reads every task when it does.

const HOWTO_KEY = "paymentSync.howToCollapsed";
const OPEN_STATUSES = ["pending", "failed"];
const REBUILD_TIMEOUT_MS = 180_000;

const usdFormat = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usd = (value: string | null | undefined) => (value == null ? "" : usdFormat.format(Number(value)));
const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;
const shortDate = (iso: string | null) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  return `${m}/${d}`;
};
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-US", {
  timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
}) : "");

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; errors?: string[] } } })?.response?.data;
  return data?.error ?? data?.errors?.join(", ") ?? (error as Error)?.message ?? "Something went wrong";
}

type ChipColor = "default" | "success" | "info" | "warning" | "error";

const STATE_COLORS: Record<string, ChipColor> = {
  ready: "success",
  not_ready: "info",
  already_done: "default",
  already_unchecked: "default",
  task_missing: "error",
  needs_milestone: "warning",
  waiting_on_ntp: "info",
  sunrise_error: "warning",
};

const RESULT_LABEL = (row: PsRow) => {
  if (row.status === "posted") return row.action === "check" ? "Checked off" : "Unchecked";
  if (row.status === "excluded") return "Excluded";
  if (row.status === "skipped") return "Already done";
  return row.status;
};

interface Choice { selected: boolean; milestone: PsMilestone | "" }
type Choices = Record<number, Choice>;

function readCollapsed(): boolean {
  try { return window.localStorage.getItem(HOWTO_KEY) === "1"; } catch { return false; }
}

function HowItWorks({ guideUrl }: { guideUrl?: string }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { window.localStorage.setItem(HOWTO_KEY, next ? "1" : "0"); } catch { /* private window */ }
  };
  const steps = [
    "Every weekday at 4:00 PM Central we pull cash customer payments and chargebacks from NetSuite and match each one to its Sunrise task: Confirm Deposit Received, Receive SOP Payment or Receive Final Payment.",
    "Ticked rows will be checked off in Sunrise (chargebacks unchecked) under your own Sunrise login. Untick anything you want left alone; unticked rows are excluded and won't come back.",
    "Rows under \"Needs a decision\" had no milestone on the payment. Pick the task from the dropdown, then tick the row.",
    "Click Post to Sunrise. Each project gets a pulse note saying who posted it and which NetSuite record it came from. Rows Sunrise already shows as done are skipped.",
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "primary.main", borderLeftWidth: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={600}>How this works</Typography>
        <IconButton size="small" onClick={toggle} aria-label={collapsed ? "Show instructions" : "Hide instructions"}>
          {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Stack>
      <Collapse in={!collapsed}>
        <Box component="ol" sx={{ m: 0, mt: 1, pl: 3, "& li": { mb: 0.75, color: "text.secondary" } }}>
          {steps.map((step) => <li key={step}><Typography variant="body2">{step}</Typography></li>)}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Only cash customers are included; Lightreach, Sungage, GoodLeap and other financier payments are left out.
          Flags in the Notes column tell you when a row needs a closer look.
          {guideUrl && (
            <>
              {" "}
              <Link href={guideUrl} target="_blank" rel="noopener noreferrer">Read the full guide</Link>.
            </>
          )}
        </Typography>
      </Collapse>
    </Paper>
  );
}

interface RowTableProps {
  rows: PsRow[];
  choices: Choices;
  setChoice: (id: number, patch: Partial<Choice>) => void;
  tasks: PsPage["tasks"];
  editable: boolean;
}

function RowTable({ rows, choices, setChoice, tasks, editable }: RowTableProps) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {editable && <TableCell padding="checkbox" />}
            <TableCell>Project</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>NetSuite</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Memo</TableCell>
            <TableCell>Task</TableCell>
            <TableCell>Sunrise</TableCell>
            <TableCell align="right">SO balance</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const choice = choices[row.id] ?? { selected: false, milestone: "" };
            const pickTask = editable && row.selectable && (!row.milestone || row.action === "uncheck");
            return (
              <TableRow key={row.id} hover sx={row.status === "failed" ? { bgcolor: "rgba(244, 67, 54, 0.08)" } : undefined}>
                {editable && (
                  <TableCell padding="checkbox">
                    {row.selectable && (
                      <Checkbox
                        size="small"
                        checked={choice.selected}
                        onChange={(e) => setChoice(row.id, { selected: e.target.checked })}
                        inputProps={{ "aria-label": `Post ${row.ns_label}` }}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {row.sunrise_url
                    ? <Link href={row.sunrise_url} target="_blank" rel="noopener noreferrer">{row.project_id}</Link>
                    : row.project_id}
                </TableCell>
                <TableCell>{row.customer_name}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {row.netsuite_url
                    ? <Link href={row.netsuite_url} target="_blank" rel="noopener noreferrer">{row.ns_label}</Link>
                    : row.ns_label}
                </TableCell>
                <TableCell>{shortDate(row.ns_date)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{usd(row.amount)}</TableCell>
                <TableCell sx={{ maxWidth: 260, color: "text.secondary" }}>{row.memo}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  {pickTask ? (
                    <Select
                      size="small"
                      fullWidth
                      displayEmpty
                      value={choice.milestone}
                      onChange={(e) => {
                        const milestone = e.target.value as PsMilestone | "";
                        setChoice(row.id, { milestone, selected: Boolean(milestone) });
                      }}
                    >
                      <MenuItem value=""><em>Pick a task</em></MenuItem>
                      {tasks.map((t) => <MenuItem key={t.milestone} value={t.milestone}>{t.task}</MenuItem>)}
                    </Select>
                  ) : (
                    <Typography variant="body2">
                      {row.action === "check" ? "Check off " : "Uncheck "}
                      {row.task_name ?? "task"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={row.state_label}
                    color={STATE_COLORS[row.sunrise_state ?? ""] ?? "default"} />
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{usd(row.so_balance)}</TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Stack spacing={0.5}>
                    {row.flag_labels.map((label) => (
                      <Tooltip key={label} title={label}>
                        <Chip size="small" color="warning" variant="outlined" icon={<WarningAmberIcon />} label={label}
                          sx={{
                            maxWidth: "100%", height: "auto", py: 0.25, justifyContent: "flex-start",
                            "& .MuiChip-label": { whiteSpace: "normal" },
                          }} />
                      </Tooltip>
                    ))}
                    {row.error && <Typography variant="caption" color="error">{row.error}</Typography>}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Section({ title, subtitle, count, defaultOpen = true, children }: {
  title: string; subtitle?: string; count: number; defaultOpen?: boolean; children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (!count) return null;
  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>{title} <Chip size="small" label={count} sx={{ ml: 1 }} /></Typography>
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <IconButton size="small" onClick={() => setOpen(!open)} aria-label={open ? `Hide ${title}` : `Show ${title}`}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
      <Collapse in={open}>{children}</Collapse>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// /payment-sync and /payment-sync/:id
// ---------------------------------------------------------------------------

export function PaymentSyncPage() {
  const { id: batchId } = useParams();
  const qc = useQueryClient();
  const queryKey = ["payment_sync", batchId ?? "current"];
  // Set while a rebuild runs: the batch id it started from and when.
  const [rebuild, setRebuild] = useState<{ from: number | null; at: number } | null>(null);
  const [notice, setNotice] = useState<{ severity: "success" | "warning" | "info"; text: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [choices, setChoices] = useState<Choices>({});

  const page = useQuery({
    queryKey,
    queryFn: () => fetchPaymentSync(batchId),
    retry: false,
    // Poll while a post runs in the background or a rebuild is on its way.
    refetchInterval: (q) => (q.state.data?.batch?.status === "posting" || rebuild ? 3000 : false),
  });
  const data = page.data;
  const batch = data?.batch ?? null;
  const rows = useMemo(() => batch?.rows ?? [], [batch]);
  // Anyone signed in can look; only payment sync posters tick and post.
  const editable = batch?.status === "open" && Boolean(data?.can_post);

  // Start from the server's ticks whenever a different batch (or a new state of
  // it) arrives: after a build, a post, or a failed post that reopened it.
  const choiceKey = batch ? `${batch.id}:${batch.status}:${batch.posted_at ?? ""}` : "";
  useEffect(() => {
    const initial: Choices = {};
    rows.forEach((row) => {
      initial[row.id] = { selected: row.selected && row.selectable, milestone: row.milestone ?? "" };
    });
    setChoices(initial);
  }, [choiceKey]);

  // A rebuild is done once the current batch is a different one.
  useEffect(() => {
    if (!rebuild || !data) return;
    if (data.batch && data.batch.id !== rebuild.from) {
      setRebuild(null);
      setNotice({ severity: "success", text: "Rebuilt from NetSuite." });
    } else if (Date.now() - rebuild.at > REBUILD_TIMEOUT_MS) {
      setRebuild(null);
      setNotice({ severity: "warning", text: "The rebuild is taking longer than expected. Refresh the page in a few minutes." });
    }
  }, [data, rebuild]);

  const setChoice = (id: number, patch: Partial<Choice>) =>
    setChoices((c) => ({ ...c, [id]: { ...(c[id] ?? { selected: false, milestone: "" }), ...patch } }));

  const groups = useMemo(() => {
    const open = rows.filter((r) => OPEN_STATUSES.includes(r.status));
    return {
      check: open.filter((r) => r.selectable && r.action === "check" && r.milestone),
      uncheck: open.filter((r) => r.selectable && r.action === "uncheck"),
      decide: open.filter((r) => r.selectable && r.action === "check" && !r.milestone),
      nothing: open.filter((r) => !r.selectable),
      results: rows.filter((r) => !OPEN_STATUSES.includes(r.status)),
    };
  }, [rows]);

  const selectable = rows.filter((r) => r.selectable && OPEN_STATUSES.includes(r.status));
  const selected = selectable.filter((r) => choices[r.id]?.selected);
  const missingTask = selected.filter((r) => !choices[r.id]?.milestone);
  const willCheck = selected.filter((r) => r.action === "check").length;
  const willUncheck = selected.filter((r) => r.action === "uncheck").length;
  const willExclude = selectable.length - selected.length;

  const post = useMutation({
    mutationFn: () => postPaymentSyncBatch(
      batch!.id,
      selectable.map((r) => ({ id: r.id, selected: Boolean(choices[r.id]?.selected), milestone: choices[r.id]?.milestone ?? "" })),
    ),
    onSuccess: (next) => {
      qc.setQueryData(queryKey, next);
      setNotice({ severity: "info", text: "Posting to Sunrise. This page updates when it's done." });
    },
  });

  const startRebuild = useMutation({
    mutationFn: rebuildPaymentSync,
    onSuccess: ({ current_batch_id }) => setRebuild({ from: current_batch_id, at: Date.now() }),
  });

  // Tell the reviewer how a post ended once the batch leaves "posting".
  const [wasPosting, setWasPosting] = useState(false);
  useEffect(() => {
    if (batch?.status === "posting") { setWasPosting(true); return; }
    if (!wasPosting || !batch) return;
    setWasPosting(false);
    const count = (s: string) => batch.rows?.filter((r) => r.status === s).length ?? 0;
    const failed = count("failed");
    const parts = [
      count("posted") && `${count("posted")} posted to Sunrise`,
      count("skipped") && `${count("skipped")} already done`,
      count("excluded") && `${count("excluded")} excluded`,
      failed && `${failed} failed`,
    ].filter(Boolean);
    setNotice({
      severity: failed || batch.error ? "warning" : "success",
      text: batch.error ? `Posting stopped: ${batch.error}` : `Done: ${parts.join(", ") || "nothing to post"}.`,
    });
  }, [batch, wasPosting]);

  return (
    <AppLayout maxWidth="xl">
      <Box sx={{ pb: editable ? 10 : 0 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5">Payments to Sunrise</Typography>
            <Typography variant="body2" color="text.secondary">
              {batch
                ? `NetSuite payments → Sunrise payment tasks. Batch for ${new Date(`${batch.run_on}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}, built ${when(batch.built_at)}`
                : "NetSuite payments matched to Sunrise payment tasks"}
              {batch?.posted_by && batch.posted_at && ` · posted by ${batch.posted_by} ${when(batch.posted_at)}`}
            </Typography>
          </Box>
          {data?.can_rebuild && (
            <Button
              variant="outlined"
              startIcon={rebuild || startRebuild.isPending ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => startRebuild.mutate()}
              disabled={Boolean(rebuild) || startRebuild.isPending || batch?.status === "posting"}
            >
              {rebuild ? "Rebuilding…" : "Rebuild from NetSuite"}
            </Button>
          )}
        </Stack>

        <>
            <HowItWorks guideUrl={data?.guide_url} />

            {data && !data.can_post && (
              <Alert severity="info" sx={{ mb: 2 }}>
                View only: posting is limited to Payments to Sunrise posters. Ask David if you should be one.
              </Alert>
            )}

            {data && !data.write_enabled && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Dry run: posting to Sunrise is switched off for now. Review the batch and tell David if anything looks wrong.
              </Alert>
            )}
            {batch?.status === "superseded" && (
              <Alert severity="info" sx={{ mb: 2 }}
                action={<Button component={RouterLink} to="/payment-sync" color="inherit" size="small">Open current batch</Button>}>
                A newer batch replaced this one. Anything still undecided here was carried into it.
              </Alert>
            )}
            {batch?.status === "posting" && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
                Posting to Sunrise as {batch.posted_by}…
              </Alert>
            )}
            {batch?.error && batch.status === "open" && (
              <Alert severity="error" sx={{ mb: 2 }}>The last post stopped: {batch.error}</Alert>
            )}
            {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
            {page.isError && <Alert severity="error" sx={{ mb: 2 }}>Couldn&apos;t load Payments to Sunrise: {errorMessage(page.error)}</Alert>}
            {startRebuild.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(startRebuild.error)}</Alert>}
            {post.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(post.error)}</Alert>}

            {page.isLoading && <Skeleton variant="rounded" height={240} />}

            {data && !batch && (
              <Paper variant="outlined" sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                No open batch. The next one is built at 4:00 PM Central on the next weekday, or rebuild it now.
              </Paper>
            )}

            {data && batch && (
              <>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
                  <Chip color="success" variant="outlined" label={`${groups.check.length} to check off`} />
                  <Chip color="error" variant="outlined" label={`${groups.uncheck.length} to uncheck`} />
                  <Chip color="warning" variant="outlined" label={`${groups.decide.length} need a decision`} />
                  <Chip variant="outlined" label={`${groups.nothing.length} nothing to do`} />
                  {groups.results.length > 0 && <Chip variant="outlined" label={`${groups.results.length} decided`} />}
                </Stack>

                <Section title="Check off in Sunrise" subtitle="Payments whose task isn't checked off yet." count={groups.check.length}>
                  <RowTable rows={groups.check} choices={choices} setChoice={setChoice} tasks={data.tasks} editable={editable} />
                </Section>
                <Section title="Uncheck in Sunrise" subtitle="Chargebacks and disputes. Confirm the task before unchecking." count={groups.uncheck.length}>
                  <RowTable rows={groups.uncheck} choices={choices} setChoice={setChoice} tasks={data.tasks} editable={editable} />
                </Section>
                <Section title="Needs a decision" subtitle="No milestone on the payment. Pick the task to check off, or leave it unticked." count={groups.decide.length}>
                  <RowTable rows={groups.decide} choices={choices} setChoice={setChoice} tasks={data.tasks} editable={editable} />
                </Section>
                <Section title="Nothing to do" subtitle="Sunrise already matches NetSuite for these." count={groups.nothing.length} defaultOpen={false}>
                  <RowTable rows={groups.nothing} choices={choices} setChoice={setChoice} tasks={data.tasks} editable={false} />
                </Section>

                <Section title="Results" count={groups.results.length}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Result</TableCell><TableCell>Project</TableCell><TableCell>Customer</TableCell>
                          <TableCell>NetSuite</TableCell><TableCell align="right">Amount</TableCell><TableCell>Task</TableCell>
                          <TableCell>By</TableCell><TableCell>When</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groups.results.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Chip size="small" variant="outlined" color={row.status === "posted" ? "success" : "default"} label={RESULT_LABEL(row)} />
                            </TableCell>
                            <TableCell>
                              {row.sunrise_url ? <Link href={row.sunrise_url} target="_blank" rel="noopener noreferrer">{row.project_id}</Link> : row.project_id}
                            </TableCell>
                            <TableCell>{row.customer_name}</TableCell>
                            <TableCell>
                              {row.netsuite_url ? <Link href={row.netsuite_url} target="_blank" rel="noopener noreferrer">{row.ns_label}</Link> : row.ns_label}
                            </TableCell>
                            <TableCell align="right">{usd(row.amount)}</TableCell>
                            <TableCell>{row.task_name}</TableCell>
                            <TableCell>{row.posted_by}</TableCell>
                            <TableCell>{when(row.posted_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Section>
              </>
            )}

            {data && data.history.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Earlier batches</Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                  {data.history.map((b) => (
                    <Chip key={b.id} component={RouterLink} to={`/payment-sync/${b.id}`} clickable variant="outlined"
                      label={`${shortDate(b.run_on)} · ${b.status} · ${plural(b.row_count, "row")}`} />
                  ))}
                </Stack>
              </Box>
            )}
        </>
      </Box>

      {data && editable && (
        <Paper elevation={8} sx={{ position: "fixed", left: 0, right: 0, bottom: 0, px: 3, py: 1.5, zIndex: 10, borderRadius: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ maxWidth: 1536, mx: "auto" }}>
            <Typography variant="body2">
              <strong>{selected.length}</strong> selected: {willCheck} to check off, {willUncheck} to uncheck. {willExclude} will be excluded.
              {missingTask.length > 0 && (
                <Typography component="span" variant="body2" color="warning.main">
                  {" "}Pick a task for {plural(missingTask.length, "ticked row")}.
                </Typography>
              )}
            </Typography>
            <Tooltip title={data.write_enabled ? "" : "Posting is switched off during the dry run"}>
              <span>
                <Button variant="contained" startIcon={post.isPending ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                  disabled={!data.write_enabled || post.isPending || missingTask.length > 0 || selectable.length === 0}
                  onClick={() => setConfirmOpen(true)}>
                  {post.isPending ? "Posting…" : "Post to Sunrise"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Post to Sunrise?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>Sunrise will be updated under your login:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <li><Typography variant="body2">{plural(willCheck, "task")} checked off</Typography></li>
            <li><Typography variant="body2">{plural(willUncheck, "task")} unchecked</Typography></li>
            <li><Typography variant="body2">{plural(willExclude, "row")} excluded (won&apos;t come back)</Typography></li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { setConfirmOpen(false); post.mutate(); }}>Post</Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
